/**
 * usePickMode.js — Modo selección de elementos en la web real.
 *
 * Desde el panel (Tienda → Diseño) el usuario puede pulsar
 * "Seleccionar elemento": la web se abre en modo búsqueda, cada elemento
 * editable se resalta, y al hacer clic vuelve al panel con la pestaña y el
 * campo correspondiente listos para editar.
 *
 * Solo disponible para admins (localStorage isAdmin === 'true').
 */
import { designDestination } from '@/utils/designContent'
import { ref, computed } from 'vue'

const PICK_KEY = 'godea_pick_mode'

function isAdminUser() {
  try {
    return localStorage.getItem('isAdmin') === 'true'
  } catch {
    return false
  }
}

export const pickMode = ref(false)
export const mobileGateOpen = ref(false)

export const isPickMode = computed(() => pickMode.value)

function isDesktopViewport() {
  try {
    return typeof window !== 'undefined' && window.innerWidth >= 1024
  } catch {
    return true
  }
}

export function enterPickMode() {
  if (!isAdminUser()) return false
  if (!isDesktopViewport()) {
    mobileGateOpen.value = true
    return false
  }
  pickMode.value = true
  try {
    localStorage.setItem(PICK_KEY, '1')
  } catch {
    /* noop */
  }
  return true
}

export function exitPickMode() {
  pickMode.value = false
  mobileGateOpen.value = false
  try {
    localStorage.removeItem(PICK_KEY)
  } catch {
    /* noop */
  }
}

/** Restaurar el modo selección al recargar la página (solo admins). */
let backListenerRegistered = false
export function initPickMode() {
  try {
    if (localStorage.getItem(PICK_KEY) === '1' && isAdminUser()) {
      pickMode.value = true
    }
  } catch {
    /* noop */
  }

  // Al volver atrás/adelante del navegador, salir del modo selección
  // para que no quede la barra inferior flotando en otra página.
  if (!backListenerRegistered && typeof window !== 'undefined') {
    backListenerRegistered = true
    window.addEventListener('popstate', () => {
      if (pickMode.value) exitPickMode()
    })
  }
}

/**
 * El usuario hizo clic en un elemento editable de la web: vuelve al panel
 * de diseño con la pestaña y el campo objetivo.
 * @param {{ tab: string, field?: string }} target
 */
export function pickElement(target) {
  exitPickMode()
  const destination = designDestination(target)
  const params = new URLSearchParams(destination.query)
  if (target?.field) params.set('field', target.field)
  const query = params.toString()
  window.location.assign(`${destination.path}${query ? `?${query}` : ''}`)
}
